import {useCallback, useEffect, useRef, useState} from 'react';
import globalDomains from '@/utils/global-domains';
import {debounce} from 'lodash';

export const sendPostMessage = (type) => {
  const message = JSON.stringify({type});
  const frame = document.getElementById('soopi-frame');
  if (frame?.contentWindow) {
    frame.contentWindow.postMessage(
      message,
      `${globalDomains.SOOPI_AFREECATV}`,
    );
  }
};

const useSearchInpFocus = (iframeVisible) => {
  const intervalIdRef = useRef(null);
  const [deleteButton, setDeleteButton] = useState(null);
  const searchInpRef = useRef(null);
  const eventListenersRef = useRef({}); // 등록된 이벤트 리스너 추적

  const debouncedPostMessage = useCallback(
    debounce((value) => {
      if (iframeVisible) {
        sendPostMessage(value ? 'entered_input' : 'empty_input');
      }
    }, 500),
    [iframeVisible, sendPostMessage],
  );

  /** 이벤트 리스너 추가 및 제거 관리 */
  const manageEventListener = useCallback((element, event, handler, action) => {
    if (!element || !handler) {
      return;
    }
    if (action === 'add') {
      element.addEventListener(event, handler);
      eventListenersRef.current[event] = handler;
    } else if (action === 'remove') {
      element.removeEventListener(event, eventListenersRef.current[event]);
      delete eventListenersRef.current[event];
    }
  }, []);

  /** 키보드 입력 핸들러 */
  const handleKeyboard = useCallback(
    (e) => {
      if (!searchInpRef.current?.value) {
        setDeleteButton(null);
      } else if (!deleteButton) {
        setDeleteButton(document.querySelector('.del_text'));
      }
      debouncedPostMessage(searchInpRef.current?.value);
    },
    [deleteButton, debouncedPostMessage],
  );

  /** 삭제 버튼 클릭 핸들러 */
  useEffect(() => {
    if (!iframeVisible) {
      return;
    }

    if (deleteButton) {
      const handleDeleteClicked = () => {
        setDeleteButton(null);
        sendPostMessage('empty_input');
      };

      manageEventListener(deleteButton, 'click', handleDeleteClicked, 'remove');
      manageEventListener(deleteButton, 'click', handleDeleteClicked, 'add');

      return () =>
        manageEventListener(
          deleteButton,
          'click',
          handleDeleteClicked,
          'remove',
        );
    }
  }, [deleteButton, iframeVisible]);

  /** 검색창 감지 및 이벤트 바인딩 */
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const searchInp = document.querySelector('#search-inp');
      if (searchInp) {
        searchInpRef.current = searchInp;

        manageEventListener(searchInp, 'input', handleKeyboard, 'remove');
        manageEventListener(searchInp, 'input', handleKeyboard, 'add');
      }
    });

    observer.observe(document.body, {childList: true, subtree: true});

    return () => {
      observer.disconnect();
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }

      // 등록된 이벤트 리스너 정리
      if (searchInpRef.current) {
        manageEventListener(
          searchInpRef.current,
          'input',
          handleKeyboard,
          'remove',
        );
      }
      if (deleteButton) {
        manageEventListener(deleteButton, 'click', null, 'remove');
      }

      eventListenersRef.current = {};
    };
  }, [iframeVisible]);

  return null;
};

export default useSearchInpFocus;
